import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import RouteCard from "../components/RouteCard";
import {
  ArrowRight,
  ArrowDown,
  Path,
  Mountains,
  Compass,
} from "@phosphor-icons/react";

export default function Home() {
  const [routes, setRoutes] = useState([]);
  const [totalRoutes, setTotalRoutes] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api
      .get("/routes", { params: { page: 1, page_size: 6 } })
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setRoutes(data);
          setTotalRoutes(data.length);
        } else {
          setRoutes(data.items || []);
          setTotalRoutes(data.total || data.items?.length || 0);
        }
      })
      .catch(() => {
        // fallback to empty
        setRoutes([]);
      });

    api
      .get("/summary")
      .then(({ data }) => {
        setSummary(data || null);
      })
      .catch(() => {
        setSummary(null);
      });
  }, []);

  const featured = routes.slice(0, 3);
  const climbedOnFilm =
    typeof summary?.total_elevation_m === "number"
      ? `${(summary.total_elevation_m/1000).toLocaleString()} km`
      : "—";
  const kilometersTraveled =
    typeof summary?.total_distance_km === "number"
      ? `${Math.round(summary.total_distance_km).toLocaleString()} km`
      : "—";

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Hero */}
      <section
        className="relative overflow-hidden grain"
        data-testid="hero-section"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1550940056-5a6306e61159"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1D1A]/85 via-[#1A1D1A]/55 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-40">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E] mb-6 flex items-center gap-3">
              <Compass size={16} weight="duotone" />
              <span>The bikepacking film atlas</span>
            </div>
            <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Ride the route
              <br />
              <span className="text-[#C86B4E] italic">before</span> you ride it.
            </h1>
            <p className="mt-8 text-white/80 text-lg max-w-xl leading-relaxed">
              Hand-curated bikepacking routes paired with rider-shot films, GPX
              tracks, and synchronized maps. Watch every climb. Click any peak.
              Discover your next adventure.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/routes"
                data-testid="hero-cta-routes"
                className="bg-[#C86B4E] text-white hover:bg-[#a85740] rounded-full px-7 py-4 font-bold inline-flex items-center gap-2 transition-colors"
              >
                Browse routes <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/explore"
                data-testid="hero-cta-explore"
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 rounded-full px-7 py-4 font-bold transition-colors"
              >
                Explore
              </Link>
              <a
                href="#featured"
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 rounded-full px-7 py-4 font-bold transition-colors"
              >
                Featured routes
              </a>
            </div>
          </div>
        </div>

        {/* stats strip */}
        <div className="relative bg-[#1A1D1A] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 grid grid-cols-3 gap-6 text-white">
            <div className="flex items-center gap-3">
              <Path size={28} weight="duotone" className="text-[#C86B4E]" />
              <div>
                <div className="text-2xl font-display font-black">
                  {totalRoutes != null ? totalRoutes : routes.length || "—"}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">
                  Routes mapped
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mountains
                size={28}
                weight="duotone"
                className="text-[#C86B4E]"
              />
              <div>
                <div className="text-2xl font-display font-black">
                  {climbedOnFilm}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">
                  Climbed on film
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Compass size={28} weight="duotone" className="text-[#C86B4E]" />
              <div>
                <div className="text-2xl font-display font-black">
                  {kilometersTraveled}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">
                  Kilometers traveled
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section
        id="featured"
        className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28"
      >
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E]">
              Featured
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 max-w-xl">
              Three routes worth packing for.
            </h2>
          </div>
          <Link
            to="/routes"
            data-testid="featured-view-all"
            className="hidden md:inline-flex items-center gap-2 text-sm font-bold text-[#2C5545] hover:text-[#1A3329]"
          >
            View all routes <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr">
          {featured.map((r, i) => (
            <RouteCard
              key={r.id}
              route={r}
              size={i === 0 ? "large" : "default"}
            />
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#1A1D1A] text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E]">
              How it works
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl mt-3">
              Watch. Plan. Ride.
            </h2>
          </div>
          {[
            {
              n: "01",
              t: "Scout the route",
              d: "Browse curated bikepacking journeys with maps, distance, elevation and difficulty.",
            },
            {
              n: "02",
              t: "Pick a film",
              d: "Each route has multiple rider-shot videos with their own GPX file and POIs.",
            },
            {
              n: "03",
              t: "Sync map ↔ video",
              d: "The map follows the video. Click anywhere on the trail to jump to that moment.",
            },
            {
              n: "04",
              t: "Explore the route",
              d: "Follow the route and discover points of interest.",
            },
            {
              n: "05",
              t: "Plan your trip",
              d: "Use the information gathered to plan your bikepacking trip.",
            },
          ].map((s) => (
            <div key={s.n} className="lg:col-span-1">
              <div className="text-[#C86B4E] font-display font-black text-5xl">
                {s.n}
              </div>
              <div className="font-display font-bold text-xl mt-2">{s.t}</div>
              <p className="text-white/70 mt-2 text-sm leading-relaxed">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
